import { useState } from 'react';
import { toast } from 'sonner';

import { ConfirmationDialog } from '@/components/ConfirmationDialog';
import { CapabilitySchedule } from '@/hooks/useCapabilitySchedule';
import { logger } from '@/utils/logger';

interface PauseScheduleModalProps {
  schedule: CapabilitySchedule | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (scheduleId: string) => Promise<void>;
}

/**
 * PauseScheduleModal - Confirmation modal for schedule pause
 *
 * Displays a confirmation message for pausing a capability schedule.
 * Unlike delete, pause is non-destructive and can be resumed later.
 */
export function PauseScheduleModal({
  schedule,
  isOpen,
  onClose,
  onConfirm,
}: PauseScheduleModalProps) {
  const [isPausing, setIsPausing] = useState(false);

  const handlePause = async () => {
    if (!schedule) return;
    setIsPausing(true);
    try {
      await onConfirm(schedule.scheduleId);
      onClose();
    } catch (error) {
      logger.error('Failed to pause schedule:', error);
      toast.error('Failed to pause schedule');
    } finally {
      setIsPausing(false);
    }
  };

  if (!schedule) return null;

  return (
    <ConfirmationDialog
      open={isOpen}
      onClose={onClose}
      onConfirm={handlePause}
      title="Pause Schedule"
      description={`Are you sure you want to pause the schedule for ${schedule.capabilityName}? You can resume the schedule at any time to restart automated executions.`}
      confirmLabel="Pause"
      loadingLabel="Pausing..."
      isLoading={isPausing}
      variant="default"
    />
  );
}
